import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { useBdArtifactDefs, useBdDetail, useBdNextStep } from '../../../hooks/useBasicDesign.js';
import { useBdViewSettings } from '../../../hooks/useBdViewSettings.js';
import { ApiError } from '../../../lib/api/_request.js';
import { moveInOrder, toggleIn } from '../../../lib/bd-view-settings.js';
import { BD_NOTES, BD_OVERVIEW, navGroups } from './bd-defs.js';
import { BdArtifactPanel } from './BdArtifactPanel.js';
import { BdNotesPanel } from './BdNotesPanel.js';
import { BdOverview } from './BdOverview.js';
import { BdProgressCard } from './BdProgressCard.js';
import { BdReviewPanel } from './BdReviewPanel.js';
import { BdSectionNav } from './BdSectionNav.js';
import type {
  BdArtifactDef,
  BdDetail,
  BdProgress,
  BdSummary,
} from '../../../lib/api/basic-design.js';

/** Backlog のチケットのページ。 @graph-connects none */
function backlogUrl(key: string): string {
  return `https://air-closet.backlog.jp/view/${key}`;
}

/** リンクの色。 @graph-connects none */
const LINK_CLASS = 'text-[var(--color-accent-primary)] hover:underline';

/**
 * 完了までの進み。API が数えた自分の要約（同じ親の基本設計の中にある）を使い、まだ 1 節も
 * 保存していない（要約が無い）ときは 0 から数える。
 * @graph-connects none
 */
function progressOf(detail: BdDetail, defs: readonly BdArtifactDef[]): BdProgress {
  const own = detail.siblings.find((s) => s.bdKey === detail.bdKey);
  return (
    own?.progress ?? {
      saved: 0,
      total: defs.length,
      requiredSaved: 0,
      requiredTotal: defs.filter((d) => d.requirement === 'required').length,
      done: false,
    }
  );
}

export interface BasicDesignViewProps {
  /** 開く基本設計のチケット */
  bdKey: string;
  /** 最初に開く節（図だけのページの札から来たとき）。無ければ次の 1 手の節 */
  initialSection?: string | undefined;
}

/**
 * 基本設計 1 件のページ。
 *
 * 大きい案件は子チケットごとに基本設計を書くので、見出しに親チケットと、同じ親の他の
 * 基本設計へのリンクを置く。その下にレビュー、全節の進み具合と次にやること、節のナビと開いた節
 * （全体の図とマトリクス、共有のメモも節と同じように開く）。ナビの並び・畳み・行の見せ方・
 * 隠した列・図の列と向きは見ている人ごとの見え方で、ブラウザにだけ残す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計 1 件のレビュー・全節の進み具合・次にやること・節のナビ・節ごとの読み書き・全体図・共有のメモを、個人の見え方つきで 1 画面で扱う
 * @graph-connects BasicDesignBody [calls] 基本設計 1 件の中身の描画
 */
export function BasicDesignView({ bdKey, initialSection }: BasicDesignViewProps) {
  // ルーターはパラメータだけが変わるとページを作り直さない。キーを付けないと、同じ親の別の
  // 基本設計へ移っても開いていた節と下書きが残り、保存するとその行が移った先に書かれる
  return <BasicDesignBody key={bdKey} bdKey={bdKey} initialSection={initialSection} />;
}

/**
 * 基本設計 1 件の中身。開いている節はこの 1 件にだけ属する。
 * @graph-connects useBdDetail [calls] 基本設計の成果物の取得
 * @graph-connects useBdArtifactDefs [calls] 節の定義の取得
 * @graph-connects useBdNextStep [calls] 次の 1 手の取得
 * @graph-connects BdProgressCard [calls] 進み具合と次にやること
 * @graph-connects BdSectionNav [calls] 節のナビ
 * @graph-connects BdArtifactPanel [calls] 節 1 つの読み書き
 * @graph-connects BdOverview [calls] 全体図・変更一覧・マトリクス
 * @graph-connects BdReviewPanel [calls] レビュー
 * @graph-connects BdNotesPanel [calls] 共有のメモ
 * @graph-connects useBdViewSettings [calls] 個人の見え方
 */
function BasicDesignBody({ bdKey, initialSection }: BasicDesignViewProps) {
  const { t } = useTranslation();
  // 選ぶまでは次の 1 手の節を開く（読み込み中はテンプレートの先頭）
  const [chosen, setChosen] = useState<string | null>(initialSection ?? null);
  const detailQuery = useBdDetail(bdKey);
  const defsQuery = useBdArtifactDefs();
  const nextStep = useBdNextStep(bdKey);
  const { settings, update } = useBdViewSettings();

  const detail = detailQuery.data;
  const defs = defsQuery.data?.items;
  const error = detailQuery.error ?? defsQuery.error;

  if (error !== null) {
    return (
      <Page>
        <p role="alert" className="text-sm text-[var(--color-text-secondary)]">
          {error instanceof ApiError ? error.message : t('bd.loadFailed')}
        </p>
      </Page>
    );
  }
  if (detail === undefined || defs === undefined) {
    return (
      <Page>
        <p className="text-sm text-[var(--color-text-muted)]">{t('bd.loading')}</p>
      </Page>
    );
  }

  const step = nextStep.data ?? null;
  const first: BdArtifactDef | undefined = defs[0];
  const fallback = step === null ? first?.key : (step.artifact ?? BD_OVERVIEW);
  const selected = chosen ?? fallback ?? BD_OVERVIEW;
  const selectedDef = defs.find((d) => d.key === selected);
  const groups = navGroups(defs, settings.order);
  const progress = progressOf(detail, defs);
  const openSection = (key: string) => {
    setChosen(key);
  };

  return (
    <Page>
      <article className="space-y-6">
        <Card padding="md">
          <CardContent className="mt-0">
            <p className="text-xs font-medium text-[var(--color-text-muted)]">
              {t('bd.header.eyebrow')}
            </p>
            <h1 className="mt-1 text-xl font-bold text-[var(--color-text-primary)]">
              {detail.bdTitle === '' ? detail.bdKey : detail.bdTitle}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--color-text-secondary)]">
              <Badge size="sm">{detail.bdKey}</Badge>
              <a
                className={LINK_CLASS}
                href={backlogUrl(detail.bdKey)}
                target="_blank"
                rel="noreferrer"
              >
                {t('bd.header.openBacklog')}
              </a>
              {detail.rootKey === detail.bdKey ? null : (
                <span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {t('bd.header.parent')}
                  </span>{' '}
                  <a
                    className={LINK_CLASS}
                    href={backlogUrl(detail.rootKey)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {detail.rootKey}
                  </a>
                </span>
              )}
            </div>
            <SiblingLinks bdKey={detail.bdKey} siblings={detail.siblings} />
          </CardContent>
        </Card>

        <BdReviewPanel bdKey={bdKey} progressDone={progress.done} onOpenSection={openSection} />

        <BdProgressCard
          defs={defs}
          saved={detail.artifacts}
          progress={progress}
          step={step}
          onSelect={setChosen}
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-[14rem_1fr]">
          <div className="md:sticky md:top-4 md:self-start">
            <BdSectionNav
              groups={groups}
              saved={detail.artifacts}
              selected={selected}
              onSelect={setChosen}
              collapsed={settings.collapsed}
              onToggleGroup={(group) => {
                update((s) => ({ ...s, collapsed: toggleIn(s.collapsed, group) }));
              }}
              onMove={(group, artifact, delta) => {
                // 並べ替えはまとまりの中だけ。ほかのまとまりの並びはそのまま残す
                const order = groups.flatMap((g) => {
                  const keys = g.defs.map((d) => d.key);
                  return g.key === group ? moveInOrder(keys, artifact, delta) : keys;
                });
                update((s) => ({ ...s, order }));
              }}
            />
          </div>
          <div className="min-w-0">
            {selected === BD_NOTES ? (
              <BdNotesPanel bdKey={bdKey} />
            ) : selectedDef === undefined ? (
              <BdOverview
                bdKey={bdKey}
                defs={defs}
                saved={detail.artifacts}
                settings={settings}
                onSettingsChange={update}
                onOpenSection={openSection}
              />
            ) : (
              // 節を切り替えたら作り直す（下書きと保存の注意を別の節に持ち越さない）
              <BdArtifactPanel
                key={selectedDef.key}
                bdKey={bdKey}
                def={selectedDef}
                saved={detail.artifacts}
                layout={settings.layouts[selectedDef.key] ?? 'cards'}
                hiddenColumns={settings.hiddenColumns[selectedDef.key] ?? []}
                onLayoutChange={(layout) => {
                  update((s) => ({ ...s, layouts: { ...s.layouts, [selectedDef.key]: layout } }));
                }}
                onToggleColumn={(column) => {
                  update((s) => ({
                    ...s,
                    hiddenColumns: {
                      ...s.hiddenColumns,
                      [selectedDef.key]: toggleIn(s.hiddenColumns[selectedDef.key] ?? [], column),
                    },
                  }));
                }}
              />
            )}
          </div>
        </div>
      </article>
    </Page>
  );
}

/** ページの枠と一覧へ戻るリンク。 @graph-connects none */
function Page({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6">
      <Link to="/projects/bd" className={`text-sm ${LINK_CLASS}`}>
        {t('bd.backToList')}
      </Link>
      {children}
    </div>
  );
}

interface SiblingLinksProps {
  /** 今開いている基本設計。リンクから外す */
  bdKey: string;
  /** 同じ親チケットの基本設計 */
  siblings: readonly BdSummary[];
}

/**
 * 同じ親チケットの他の基本設計へのリンク。自分しかいなければ出さない。
 * @graph-connects none
 */
function SiblingLinks({ bdKey, siblings }: SiblingLinksProps) {
  const { t } = useTranslation();
  const others = siblings.filter((s) => s.bdKey !== bdKey);
  if (others.length === 0) return null;

  return (
    <div className="mt-4 border-t border-[var(--color-border)] pt-3">
      <p className="text-xs text-[var(--color-text-muted)]">{t('bd.header.siblings')}</p>
      <ul aria-label={t('bd.header.siblings')} className="mt-2 flex flex-wrap gap-2">
        {others.map((s) => (
          <li key={s.bdKey}>
            <Link
              to="/projects/bd/$bdKey"
              params={{ bdKey: s.bdKey }}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1 text-xs text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)]"
            >
              <span className="font-medium">{s.bdKey}</span>
              {s.bdTitle === '' ? null : (
                <span className="text-[var(--color-text-secondary)]">{s.bdTitle}</span>
              )}
              <span className="text-[var(--color-text-muted)]">
                {s.progress.saved}/{s.progress.total}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
